/*
 * Due Diligence Workbench — state persistence.
 *
 * Two storage keys:
 *   - motr-ddw-state-v1   the full state (lens + vendors + phase1Complete)
 *   - motr-lens-v1        the SHARED lens (subset; cross-tool with Plan Builder)
 *
 * Hydration order on load (matches BUILD-INSTRUCTIONS §7):
 *   1. Read motr-ddw-state-v1  →  baseline state with backfilled defaults.
 *   2. Read motr-lens-v1       →  if present, override the shared fields.
 *   3. If shared is empty but local has content, write local up to shared.
 *
 * Every persist() saves both keys so the lens stays in sync.
 */

import {
  emptyLens as emptySharedLens,
  lensHasContent,
  readSharedLens,
  writeSharedLens,
  type SharedLens,
} from '@shared/lens';

import type { DdwLens, DdwState, DdwVendor } from './types';

const STORAGE_KEY = 'motr-ddw-state-v1';

export function emptyDdwState(): DdwState {
  const now = new Date().toISOString();
  return {
    version: 1,
    createdAt: now,
    updatedAt: now,
    lens: emptyDdwLens(),
    vendors: [],
    phase1Complete: false,
  };
}

export function emptyDdwLens(): DdwLens {
  return {
    jobStatement: '',
    redFlagAcknowledged: false,
    redFlagWordsHit: [],
    frictionPhase: null,
    outcomes: ['', '', ''],
    emotional: { feel: [], avoid: [] },
    flexibility: null,
    changeInventoryTicked: [],
    tax: { routine: 18, workarounds: 8, major: 1, calculatedAnnual: 0 },
  };
}

/**
 * Load the DDW state from localStorage, hydrate the shared lens fields
 * from `motr-lens-v1`, and return the merged state. If nothing is
 * stored, returns a fresh empty state.
 */
export function loadDdwState(): DdwState {
  const local = readLocal();
  const shared = readSharedLens();

  if (shared) {
    overlaySharedLens(local, shared);
  } else if (lensHasContent(asSharedLens(local.lens))) {
    // One-time migration: bring up local lens to the shared key.
    writeSharedLens({ ...asSharedLens(local.lens), source: 'workbench' });
  }
  return local;
}

/** Persist state to its own key and mirror the lens to the shared key. */
export function persistDdwState(state: DdwState): void {
  state.updatedAt = new Date().toISOString();
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* storage full / disabled */
  }
  writeSharedLens({ ...asSharedLens(state.lens), source: 'workbench' });
}

export function clearDdwState(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

// ─── Internals ───────────────────────────────────────────────

function readLocal(): DdwState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return emptyDdwState();
    const parsed = JSON.parse(raw);
    return migrate(parsed);
  } catch {
    return emptyDdwState();
  }
}

function migrate(input: unknown): DdwState {
  if (!input || typeof input !== 'object') return emptyDdwState();
  const obj = input as Record<string, unknown>;
  const fresh = emptyDdwState();

  const merged: DdwState = {
    ...fresh,
    ...(obj as Partial<DdwState>),
    version: 1,
  };
  // Lens shape backfill — be liberal with old saves.
  const inLens = (obj.lens ?? {}) as Partial<DdwLens>;
  merged.lens = {
    ...fresh.lens,
    ...inLens,
    outcomes: normaliseOutcomes(inLens.outcomes),
    redFlagWordsHit: Array.isArray(inLens.redFlagWordsHit) ? inLens.redFlagWordsHit : [],
    changeInventoryTicked: Array.isArray(inLens.changeInventoryTicked)
      ? inLens.changeInventoryTicked
      : [],
    emotional: {
      feel: Array.isArray(inLens.emotional?.feel) ? (inLens.emotional!.feel as string[]) : [],
      avoid: Array.isArray(inLens.emotional?.avoid) ? (inLens.emotional!.avoid as string[]) : [],
    },
    tax: {
      routine: inLens.tax?.routine ?? fresh.lens.tax.routine,
      workarounds: inLens.tax?.workarounds ?? fresh.lens.tax.workarounds,
      major: inLens.tax?.major ?? fresh.lens.tax.major,
      calculatedAnnual: inLens.tax?.calculatedAnnual ?? 0,
    },
  };

  // Vendors — drop scored flag if old shape is missing new fields, so the
  // buyer is prompted to fill in (matches prototype's defensive load).
  merged.vendors = Array.isArray(obj.vendors) ? (obj.vendors as DdwVendor[]) : [];
  for (const v of merged.vendors) {
    if (v.scored && v.score) {
      const s = v.score;
      const needsRescore =
        s.scaleOut == null ||
        s.scaleUp == null ||
        s.emotionalFit == null ||
        !s.adoption ||
        !s.spendTier;
      if (needsRescore) v.scored = false;
    }
  }

  merged.phase1Complete = !!obj.phase1Complete;
  return merged;
}

function normaliseOutcomes(arr: unknown): [string, string, string] {
  const safe: string[] = Array.isArray(arr) ? (arr as unknown[]).map((x) => (typeof x === 'string' ? x : '')) : [];
  return [safe[0] ?? '', safe[1] ?? '', safe[2] ?? ''];
}

function overlaySharedLens(state: DdwState, shared: SharedLens): void {
  state.lens.jobStatement = shared.jobStatement;
  state.lens.redFlagWordsHit = [...shared.redFlagWordsHit];
  state.lens.frictionPhase = shared.frictionPhase;
  state.lens.outcomes = [
    shared.outcomes[0] ?? '',
    shared.outcomes[1] ?? '',
    shared.outcomes[2] ?? '',
  ];
  state.lens.emotional = {
    feel: [...shared.emotional.feel],
    avoid: [...shared.emotional.avoid],
  };
  state.lens.flexibility = shared.flexibility;
}

function asSharedLens(lens: DdwLens): Omit<SharedLens, 'version' | 'updatedAt'> {
  return {
    source: 'workbench',
    jobStatement: lens.jobStatement,
    redFlagWordsHit: lens.redFlagWordsHit,
    frictionPhase: lens.frictionPhase,
    outcomes: lens.outcomes,
    emotional: { feel: lens.emotional.feel, avoid: lens.emotional.avoid },
    flexibility: lens.flexibility,
  };
}

// Re-export helpers some consumers want.
export { emptySharedLens };
